import {
  Box,
  Dialog,
  InputAdornment,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { forwardRef } from 'react';
import { CloseIcon } from '../../assets/icons/icons';
import Button from '../../components/Button';
import TextInput from './TextInput';
import SelectInput from './SelectInput';
import Tooltip from './Tooltip';
import InlineLink from '../../components/InlineLink';
import useFilters from './hooks/useFilters';

const RoomsWizardModal = forwardRef(({ open, filters, onClose, onUpdate }, ref) => {
  const {
    working,
    setWorking,
    zipcodeError,
    negativeIncomeError,
    updateReady,
    householdSizeOptions,
  } = useFilters(filters);

  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog
      ref={ref}
      open={open}
      onClose={onClose}
      fullScreen={isSmallScreen}
      aria-labelledby="rooms-wizard-heading"
    >
      <Box p={4} pb={0} sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', right: '0', top: '0' }}>
          <Button onClick={onClose} sx={{ minWidth: 'auto' }} aria-label="Close filters">
            <CloseIcon />
          </Button>
        </Box>
        <Typography
          id="rooms-wizard-heading"
          sx={{
            fontWeight: '500',
            fontSize: '22px',
            lineHeight: '100%',
            letterSpacing: '0%',
            textAlign: 'center',
            verticalAlign: 'middle',
          }}
          mb={2}
        >
          Input your information to determine which incentives you qualify for
        </Typography>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        sx={{ height: '100%' }}
      >
        <Box p={2}>
          <Stack gap={2} mb={2}>
            {/* Good to know: 94401 is a good test Zip */}
            <TextInput
              label="What's your zip code?"
              value={working.zip}
              onChange={(e) => setWorking({ zip: String(e.target.value).trim().substr(0, 5) })}
              errorMessage={zipcodeError && 'Choose a Zip Code in PG&E territory'}
              placeholder="i.e. 94102"
            />
            <SelectInput
              label="Household Size"
              value={working.household_size}
              onChange={(e) => setWorking({ household_size: e.target.value })}
              options={householdSizeOptions}
              placeholder="Choose your household size"
            />
            <TextInput
              label="Yearly Household Income"
              sublabel={
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography
                    sx={{
                      fontWeight: '400',
                      fontSize: '10px',
                      lineHeight: '100%',
                      letterSpacing: '0px',
                    }}
                  >
                    Why are we asking for this information?
                  </Typography>
                  <Box>
                    <Tooltip title="Some federal, state, or utility incentives are limited by yearly household income." />
                  </Box>
                </Box>
              }
              value={working.income}
              onChange={(e) =>
                setWorking({ income: String(e.target.value).trim().replace(/,/g, '') })
              }
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>,
              }}
              errorMessage={negativeIncomeError && 'Set an annual income'}
              placeholder="i.e. 50000"
            />
          </Stack>
        </Box>
        <Box p={2}>
          <Button
            variant="contained"
            sx={{
              boxShadow: 'none',
              backgroundColor: 'orange.500',
              color: 'blue.900',
              borderRadius: 0,
              textTransform: 'none',
            }}
            fullWidth
            onClick={() => onUpdate(working)}
            disabled={!updateReady}
          >
            Get Started Now
          </Button>

          <Box mt={2}>
            <Typography
              sx={{
                fontWeight: '400',
                fontSize: '12px',
                lineHeight: '20px',
                letterSpacing: '0%',
              }}
            >
              Disclaimer: By submitting this form the financial incentives that may apply to your
              situation will be displayed. Services provided by{' '}
              <InlineLink href="https://www.rewiringamerica.org">Rewiring America</InlineLink>, view
              terms and conditions{' '}
              <InlineLink href="https://www.rewiringamerica.org/terms">here</InlineLink>.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
});

RoomsWizardModal.displayName = 'RoomsWizardModal';

export default RoomsWizardModal;
